import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendGeocodeCoords, setDeliveryPolygons, setGeocodeLoading, setIsInsidePolygon, setMapCenter, setMapZoom } from '../../../../redux/reducer/slices/mapSlice';
import { YMaps, Map, Polygon } from 'react-yandex-maps';
import styled from 'styled-components';

const MapContainer = ({ setDeliveryInfo }) => {
    const dispatch = useDispatch();
    const { currentCity, deliveryZones } = useSelector((state) => state.common);
    const { mapCenter, mapZoom, deliveryPolygons, isHouse, geocodeCoords, isInsidePolygon } = useSelector((state) => state.map);
    const mapRef = useRef(null);
    const polygonRefs = useRef([]);
    const debounceTimerRef = useRef(null);
    const isProgrammaticActionRef = useRef(false);
    const latestMapZoomRef = useRef(mapZoom);
    const [dragging, setDragging] = useState(false);

    const setProgrammaticView = useCallback((center, zoom) => {
        if (!mapRef.current) return;
        isProgrammaticActionRef.current = true;
        // setCenter(center, zoom) — один action вместо двух
        mapRef.current.setCenter(center, zoom, { duration: 500 });
    }, []);

    // Чистим debounce при размонтировании
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, []);

    // Храним актуальный zoom в ref, чтобы эффекты не зависели от mapZoom
    // (иначе при пользовательском зуме эффект может сделать setCenter и “откатить” центр назад).
    useEffect(() => {
        latestMapZoomRef.current = mapZoom;
    }, [mapZoom]);

    // Устанавка текущей зоны доставки взависимости от id города
    useEffect(() => {
        if (currentCity && deliveryZones) {
            const currentDeliveryZone = deliveryZones.find(city => +city.id === +currentCity.id); 

            if(currentDeliveryZone && currentDeliveryZone.zones && currentDeliveryZone.zones.length > 0) {
                dispatch(setDeliveryPolygons(currentDeliveryZone.zones));
                dispatch(setMapCenter([currentDeliveryZone.latitude, currentDeliveryZone.longitude]));
            }
        }
    }, [currentCity, deliveryZones, dispatch]);

    // Программное позиционирование карты (смена города/центра из redux)
    // ВАЖНО: не реагируем на mapZoom, чтобы пользовательский зум не приводил к setCenter обратно.
    useEffect(() => {
        if (!mapRef.current) return;
        setProgrammaticView(mapCenter, latestMapZoomRef.current);
    }, [mapCenter, setProgrammaticView]);

    // Если геокод дал точку здания — центрируемся на точные координаты
    // Не зависим от mapZoom, чтобы последующие пользовательские зумы не "прилипали" к geocodeCoords.
    useEffect(() => {
        if (!mapRef.current || !geocodeCoords || !isHouse) return;
        setProgrammaticView(geocodeCoords, latestMapZoomRef.current);
    }, [isHouse, geocodeCoords, setProgrammaticView]);

    // Сброс ссылок на полигоны при смене зон
    useEffect(() => {
        polygonRefs.current = [];
    }, [deliveryPolygons]);

        useEffect(() => {
                if (!geocodeCoords || !polygonRefs.current.length) return;

                let insideIndex = -1;
                for (let i = 0; i < polygonRefs.current.length; i += 1) {
                        const poly = polygonRefs.current[i];
                        if (poly?.geometry?.contains?.(geocodeCoords)) {
                                insideIndex = i;
                                break;
                        }
                }

                const inside = insideIndex !== -1;
                dispatch(setIsInsidePolygon(inside));

                if (!inside) {
                        setDeliveryInfo(null);
                        return;
                }

                const zone = deliveryPolygons[insideIndex];
                if (!zone) {
                        setDeliveryInfo(null);
                        return;
                }

                setDeliveryInfo({
                        deliveryPrices: zone.deliveryConditions,
                        minOrderPrice: zone.minDeliveryPrice
                });
        }, [geocodeCoords, deliveryPolygons, dispatch, setDeliveryInfo]);

    const handleBoundsChange = (e) => {
        const newZoom = e.get('newZoom');
        // boundsChange может срабатывать и на программные изменения
        if (isProgrammaticActionRef.current) return;
        if (typeof newZoom === 'number') {
            dispatch(setMapZoom(newZoom));
        }
    };

    const handleMapActionStart = () => {
        if (isProgrammaticActionRef.current) {
            return;
        }

        setDragging(true);
        dispatch(setGeocodeLoading(true));
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    const handleMapActionEnd = () => {
        if (isProgrammaticActionRef.current) {
            isProgrammaticActionRef.current = false;
            setDragging(false);
            dispatch(setGeocodeLoading(false));
            return;
        }

        if (!mapRef.current) {
            setDragging(false);
            dispatch(setGeocodeLoading(false));
            return;
        }

        const newCenter = mapRef.current.getCenter();
        debounceTimerRef.current = setTimeout(() => {
            dispatch(sendGeocodeCoords(newCenter));
        }, 1000);

        setDragging(false);
    };

    const polygons = useMemo(() => {
        return deliveryPolygons.map((zone) => ({
            geometry: [
                zone.coords.map(coord => [coord.lat, coord.lon]),
            ],
            options: {
                fillColor: mapZoom === 14
                    ? 'rgba(0, 180, 0, 0.2)'
                    : mapZoom === 15
                        ? 'rgba(0, 180, 0, 0.1)'
                        : mapZoom >= 16
                            ? 'rgba(0, 180, 0, 0)'
                            : 'rgba(0, 180, 0, 0.3)',
                strokeColor: zone.borderColor,
                strokeWidth: 2,
                strokeOpacity: 0.8,
                zIndex: 2,
            },
            properties: {
                hintContent: zone.name,
            },
        }));
    }, [deliveryPolygons, mapZoom]);

    return (
        <MapWrapper>
            <YMaps
                query={{
                    apikey: '32a35624-b004-474d-b343-426da5feddae',
                    lang: 'ru_RU',
                    load: 'package.full',
                }}
            >
                <Map
                    instanceRef={(ref) => {
                        if (ref) {
                            mapRef.current = ref;
                        }
                    }}
                    defaultState={{
                        center: mapCenter,
                        zoom: mapZoom,
                        yandexMapDisablePoiInteractivity: true,
                        controls: [],
                    }}
                    options={{
                        scrollZoom: false,
                        dblClickZoom: false,
                        suppressMapOpenBlock: true,
                        tileQuality: 'low',
                    }}
                    width='100%'
                    height='100%'
                    onBoundsChange={handleBoundsChange} 
                    onActionBegin={handleMapActionStart}
                    onActionEnd={handleMapActionEnd}
                >
                    {polygons && polygons.map((polygon, index) => (
                        <Polygon
                            key={index}
                            instanceRef={(ref) => {
                                polygonRefs.current[index] = ref; // ✅ сохраняем ссылку на каждый полигон
                            }}
                            geometry={polygon.geometry}
                            options={polygon.options}
                            properties={polygon.properties}
                        />
                    ))}
                </Map>
            </YMaps>

            <CentralPlacemarkWrapper dragging={dragging}>
                <CentralPlacemarkGreen dragging={dragging} success={isInsidePolygon} error={isInsidePolygon === false}/>
            </CentralPlacemarkWrapper>
            
        </MapWrapper>
    )
}

export default MapContainer;

const CentralPlacemarkGreen = ({dragging, success, error}) => (
    <svg width={window.innerWidth <= 748 ? "61" : "93"} height={window.innerWidth <= 748 ? "82" : "127"}  viewBox="0 0 93 127" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse style={{opacity: !dragging ? 1 : 0, transition: 'all .2s'}} cx="46.5" cy="105.909" rx="5.72727" ry="1.43182" fill="#2C2E32"/>
        <g filter="url(#filter0_d_1454_9979)">
            <mask id="path-2-outside-1_1454_9979" maskUnits="userSpaceOnUse" x="13" y="18" width="67" height="90" fill="black">
                <rect fill="white" x="13" y="18" width="67" height="90"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M47.9318 85.7064C47.9318 84.1747 49.1417 82.9281 50.6603 82.7277C66.0909 80.6917 78 67.4866 78 51.5C78 34.103 63.897 20 46.5 20C29.103 20 15 34.103 15 51.5C15 67.4866 26.9091 80.6917 42.3397 82.7277C43.8583 82.9281 45.0682 84.1747 45.0682 85.7064L45.0682 104.477C45.0682 105.268 45.7092 105.909 46.5 105.909C47.2908 105.909 47.9318 105.268 47.9318 104.477L47.9318 85.7064Z"/>
            </mask>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M47.9318 85.7064C47.9318 84.1747 49.1417 82.9281 50.6603 82.7277C66.0909 80.6917 78 67.4866 78 51.5C78 34.103 63.897 20 46.5 20C29.103 20 15 34.103 15 51.5C15 67.4866 26.9091 80.6917 42.3397 82.7277C43.8583 82.9281 45.0682 84.1747 45.0682 85.7064L45.0682 104.477C45.0682 105.268 45.7092 105.909 46.5 105.909C47.2908 105.909 47.9318 105.268 47.9318 104.477L47.9318 85.7064Z" fill="url(#paint0_linear_1454_9979)" shape-rendering="crispEdges"/>
            <path d="M45.0682 104.477H43.6364H45.0682ZM47.9318 104.477H46.5H47.9318ZM42.3397 82.7277L42.527 81.3082L42.3397 82.7277ZM50.6603 82.7277L50.473 81.3082L50.6603 82.7277ZM47.9318 85.7064H49.3636H47.9318ZM76.5682 51.5C76.5682 66.7588 65.2008 79.365 50.473 81.3082L50.8476 84.1472C66.981 82.0185 79.4318 68.2144 79.4318 51.5H76.5682ZM46.5 21.4318C63.1062 21.4318 76.5682 34.8938 76.5682 51.5H79.4318C79.4318 33.3123 64.6877 18.5682 46.5 18.5682V21.4318ZM16.4318 51.5C16.4318 34.8938 29.8938 21.4318 46.5 21.4318V18.5682C28.3123 18.5682 13.5682 33.3123 13.5682 51.5H16.4318ZM42.527 81.3082C27.7992 79.365 16.4318 66.7588 16.4318 51.5H13.5682C13.5682 68.2144 26.019 82.0185 42.1524 84.1472L42.527 81.3082ZM46.5 104.477L46.5 85.7064H43.6364L43.6364 104.477H46.5ZM46.5 104.477V104.477H43.6364C43.6364 106.059 44.9184 107.341 46.5 107.341V104.477ZM46.5 104.477V104.477V107.341C48.0816 107.341 49.3636 106.059 49.3636 104.477H46.5ZM46.5 85.7064L46.5 104.477H49.3636V85.7064H46.5ZM42.1524 84.1472C43.0368 84.2639 43.6364 84.969 43.6364 85.7064H46.5C46.5 83.3803 44.6798 81.5922 42.527 81.3082L42.1524 84.1472ZM50.473 81.3082C48.3202 81.5922 46.5 83.3803 46.5 85.7064H49.3636C49.3636 84.969 49.9632 84.2639 50.8476 84.1472L50.473 81.3082Z" fill="url(#paint1_linear_1454_9979)" mask="url(#path-2-outside-1_1454_9979)"/>
        </g>
        <circle cx="46.4999" cy="51.5" r="12.8864" fill="white"/>
        <defs>
            <filter id="filter0_d_1454_9979" x="0.681752" y="11.4091" width="91.6365" height="114.545" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="5.72727"/>
                <feGaussianBlur stdDeviation="6.44318"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1454_9979"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1454_9979" result="shape"/>
            </filter>
            <linearGradient id="paint0_linear_1454_9979" x1="25.7386" y1="33.6023" x2="68.6932" y2="111.636" gradientUnits="userSpaceOnUse">
                <stop offset="0.0953635" stop-color={success ? "#86c370" : error ? "#ee6371" : "#c3bf70"}/>
                <stop offset="1" stop-color={success ? "#59aa3e" : error ? "#EA394B" : "#aaaa3e"}/>
            </linearGradient>
            <linearGradient id="paint1_linear_1454_9979" x1="23.5909" y1="3.53409" x2="46.5" y2="70.1136" gradientUnits="userSpaceOnUse">
                <stop stop-color="white"/>
                <stop offset="1" stop-color="white" stop-opacity="0"/>
            </linearGradient>
        </defs>
    </svg>    
);

const MapWrapper = styled.div`
    position: relative;
    width: 100%;
    height: calc(80vh - 200px);
`

const CentralPlacemarkWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, ${({dragging}) => dragging ? '-90%' : '-80%'});
    transition: all .5s;
    user-select: none;
    pointer-events: none;
    @media (max-width: 748px) {
        top: 50%;
    }
`
